import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subject, take, takeUntil } from 'rxjs';

import {
  ClinicAppointmentService,
  CacheService,
  RatingService,
  BodyScanAppointmentService,
} from 'app-service';
import { Button } from 'app-core';
import { DialogService } from 'app-shared-ui';

import ButtonType = Data.Core.ButtonType;
import IconType = Data.Core.IconType;
import { MajorCategories } from '@alpha/types';

@Component({
  selector: 'app-appointment-tracking',
  templateUrl: './appointment-tracking.component.html',
  styleUrls: ['./appointment-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DialogService, RatingService],
})
export class AppointmentTrackingComponent implements OnInit, OnDestroy {
  @ViewChild('appointmentStatusErrorMsg', { static: true })
  public errorMsg!: TemplateRef<any>;
  @ViewChild('footerMsg', { static: true })
  public footerMsg!: TemplateRef<any>;
  public clinicAppointmentService = inject(ClinicAppointmentService);
  public bodyScanAppointmentService = inject(BodyScanAppointmentService);
  public appointment$: any;
  private countdown = 240; // Initial countdown value
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  public refreshStatusButton: Button = new Button({
    type: ButtonType.flatButton,
    label: 'Refresh',
    action: () => {
      const savedCountdown =
        this._cacheService.getItem<string>('countdownValue');
      if (!parseInt(savedCountdown || '', 10)) {
        this.ngOnInit();
      }
      interval(1000) // Emit value every second
        .pipe(take(this.countdown)) // Limit the emissions to the countdown duration
        .subscribe({
          next: () => {
            if (this.countdown > 0) {
              this.refreshStatusButton.disabled = () => true;
              this.countdown--; // Reduce countdown every second
              this.refreshStatusButton.label = `${this.countdown}`;
              this._cacheService.setItem(
                'countdownValue',
                this.countdown.toString(),
              );
              this._cdRef.markForCheck();
            }
          },
          complete: () => {
            this._cacheService.removeItem('countdownValue');
            setTimeout(() => {
              this.countdown = 240;
              this.refreshStatusButton.label = 'Refresh';
              this.refreshStatusButton.disabled = () => false;
              this._cdRef.markForCheck();
            }, 2000);
          }, // Reset after 2 seconds
        });
    },
    icon: IconType.refreshIcon,
    disabled: () => false,
  });

  public constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _cacheService: CacheService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _route: Router,
  ) {}

  public ngOnInit(): void {
    if (this._route.url.split('/')[2] === 'view') {
      // Extract existing query params
      if (
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.Doctor)
      ) {
        this.trackClinicAppointment(
          this._route.url.split('/')[6].split('?')[0],
        );
      }

      if (
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.BodyScan)
      ) {
        this.trackBodyScanAppointment(
          this._route.url.split('/')[6].split('?')[0],
        );
      }
    }

    this.loadCountdown(); // Load countdown when the page loads

    this._cdRef.markForCheck();
  }

  private loadCountdown(): void {
    const savedCountdown = this._cacheService.getItem<string>('countdownValue');
    this.countdown = savedCountdown ? parseInt(savedCountdown, 10) : 240;
  }

  private trackBodyScanAppointment(appointmentId: string): void {
    this.bodyScanAppointmentService
      .trackAppointment(appointmentId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        this.appointment$ = res.status;
        this._cdRef.markForCheck();
      });
  }

  private trackClinicAppointment(appointmentId: string): void {
    this.clinicAppointmentService
      .trackAppointment(appointmentId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        this.appointment$ = res.status;
        this._cdRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
